import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../navbar/Navbar";
import { Box, Heading, Text } from "@chakra-ui/react";

export default function BookCounsellor() {
  const [counsellors, setCounsellors] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState({});
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    axios
      .get("https://happyu.onrender.com/counsellors")
      .then((res) => setCounsellors(res.data))
      .catch((err) => console.error(err));
  }, [navigate, user]);

  const today = new Date().toISOString().split("T")[0];

  const handleSlotChange = (id, field, value) => {
    setSelectedSlots((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const book = async (counsellorId) => {
    if (!user || !user._id) {
      alert("Please log in to book an appointment.");
      navigate("/login");
      return;
    }
    const slot = selectedSlots[counsellorId] || {};
    const payload = {
      userId: user._id,
      username: user.username,
      counsellorId,
      date: slot.date || today,
      time: slot.time || "17:00",
    };
    try {
      await axios.post("https://happyu.onrender.com/appointments/book", payload);
      alert("Appointment requested!");
    } catch (err) {
      console.error(err);
      alert("Failed to request appointment.");
    }
  };

  const addReview = async (counsellorId) => {
    const slot = selectedSlots[counsellorId] || {};
    const rating = parseInt(slot.rating || 5, 10);
    const comment = (slot.review || "").trim();
    if (!comment) {
      alert("Please enter a review comment.");
      return;
    }

    // Optimistically update UI
    setCounsellors((prev) =>
      prev.map((c) =>
        c._id === counsellorId
          ? {
              ...c,
              reviews: [
                ...(c.reviews || []),
                { user: user?.name || "You", rating, comment, _id: Date.now() },
              ],
            }
          : c
      )
    );

    try {
      await axios.post(
        `https://happyu.onrender.com/counsellors/${counsellorId}/reviews`,
        { userId: user?._id, rating, comment }
      );
    } catch (err) {
      // If endpoint doesn't exist, ignore — UI already updated optimistically
      console.warn("Could not POST review to backend:", err.message || err);
    }

    // Clear the review input for that counsellor
    setSelectedSlots((prev) => ({
      ...prev,
      [counsellorId]: { ...prev[counsellorId], review: "", rating: 5 },
    }));
  };

  const avgRating = (reviews = []) =>
    reviews.length
      ? (
          reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length
        ).toFixed(1)
      : "N/A";

  const initials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <>

      <Box p={6} maxW="6xl" mx="auto" mt={2} textAlign="center">
        <Heading mb={6} color="blue.700">
          Welcome, {user?.name || "Guest"}
        </Heading>
        <Text mb={6} mx="auto" color="gray.600">
          Find and book a counsellor that fits your needs
        </Text>
      </Box>

      <div className="p-6 max-w-5xl mx-auto mt-0">
        <div className="grid gap-6 md:grid-cols-2">
          {counsellors.map((c) => {
            const slot = selectedSlots[c._id] || {};
            return (
              <div
                key={c._id}
                className="bg-white shadow-lg rounded-lg overflow-hidden border"
              >
                <div className="flex p-4 gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                      {initials(c.name)}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold">{c.name}</h3>
                        <p className="text-sm text-gray-500">
                          {c.specialty || "General Counselling"}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-yellow-500 font-semibold">
                          {avgRating(c.reviews)} ★
                        </div>
                        <div className="text-xs text-gray-400">
                          {(c.reviews || []).length} reviews
                        </div>
                      </div>
                    </div>

                    <p className="mt-3 text-sm text-gray-600">
                      {c.bio ||
                        "Experienced counsellor helping clients with emotional well-being."}
                    </p>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">Date</label>
                        <input
                          type="date"
                          className="mt-1 w-full border rounded px-2 py-1 text-sm"
                          value={slot.date || today}
                          onChange={(e) =>
                            handleSlotChange(c._id, "date", e.target.value)
                          }
                          min={today}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Time</label>
                        <input
                          type="time"
                          className="mt-1 w-full border rounded px-2 py-1 text-sm"
                          value={slot.time || "17:00"}
                          onChange={(e) =>
                            handleSlotChange(c._id, "time", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => book(c._id)}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded font-medium"
                      >
                        Book Appointment
                      </button>
                    </div>

                    <div className="mt-4">
                      <h4 className="text-sm font-medium">Add a review</h4>
                      <div className="mt-2 grid grid-cols-6 gap-2">
                        <select
                          className="col-span-1 border rounded px-2 py-1 text-sm"
                          value={slot.rating || 5}
                          onChange={(e) =>
                            handleSlotChange(c._id, "rating", e.target.value)
                          }
                        >
                          <option value={5}>5★</option>
                          <option value={4}>4★</option>
                          <option value={3}>3★</option>
                          <option value={2}>2★</option>
                          <option value={1}>1★</option>
                        </select>
                        <textarea
                          className="col-span-5 border rounded px-2 py-1 text-sm"
                          placeholder="Write a short review..."
                          value={slot.review || ""}
                          onChange={(e) =>
                            handleSlotChange(c._id, "review", e.target.value)
                          }
                        />
                      </div>
                      <div className="mt-2 text-right">
                        <button
                          onClick={() => addReview(c._id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Submit Review
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reviews */}
                <div className="bg-gray-50 p-3 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Recent reviews</span>
                    <span className="text-xs text-gray-400">
                      {(c.reviews || []).length} total
                    </span>
                  </div>
                  <ul className="space-y-2 max-h-36 overflow-auto">
                    {(c.reviews || [])
                      .slice()
                      .reverse()
                      .slice(0, 3)
                      .map((r) => (
                        <li key={r._id || Math.random()} className="text-sm">
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold">
                              {r.user || "Anonymous"}
                            </div>
                            <div className="text-xs text-yellow-500">
                              {r.rating}★
                            </div>
                          </div>
                          <div className="text-xs text-gray-600">
                            {r.comment}
                          </div>
                        </li>
                      ))}
                    {(c.reviews || []).length === 0 && (
                      <li className="text-xs text-gray-500">
                        No reviews yet — be the first to review!
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
